import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Copy, Terminal, ExternalLink } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import copyToClipboard from 'copy-to-clipboard';

interface CodeAgentInstallProps {
    apiKey?: string;
    className?: string;
}

export function CodeAgentInstall({
    apiKey: providedApiKey,
    className = ""
}: CodeAgentInstallProps) {
    const { toast } = useToast();


    const handleCopyToClipboard = (text: string, successMessage: string = "Copied to clipboard!") => {
        const success = copyToClipboard(text);

        if (success) {
            toast({
                title: "Success",
                description: successMessage,
            });
        } else {
            toast({
                title: "Error",
                description: "Failed to copy to clipboard. Please copy manually.",
                variant: "destructive",
            });
        }
    };

    const geminiCommand = providedApiKey
        ? `gemini mcp add --transport http plusE https://mcp.plusefin.com/mcp?apikey=${providedApiKey}`
        : 'gemini mcp add --transport http plusE https://mcp.plusefin.com/mcp?apikey=YOUR_API_KEY';

    const claudeCommand = providedApiKey
        ? `claude mcp add --transport http plusE https://mcp.plusefin.com/mcp?apikey=${providedApiKey}`
        : 'claude mcp add --transport http plusE https://mcp.plusefin.com/mcp?apikey=YOUR_API_KEY';

    return (
        <Card className={`w-full overflow-hidden bg-white/80 shadow-sm ${className}`}>
            <CardHeader className="border-b border-stone-100">
                <CardTitle className="flex items-center gap-2">
                    <Terminal className="w-5 h-5 text-stone-500" />
                    Code Agent Setup
                </CardTitle>
                <CardDescription>
                    Quick setup commands for popular code editors and AI assistants
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Gemini CLI Command */}
                {/* Gemini CLI Command */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-stone-950">Gemini CLI</h4>
                            <a
                                href="https://github.com/google-gemini/gemini-cli"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-stone-500 transition-colors hover:text-stone-950"
                            >
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyToClipboard(geminiCommand, "Gemini CLI command copied!")}
                            className="h-8 rounded-full px-2"
                        >
                            <Copy className="w-4 h-4" />
                        </Button>
                    </div>
                    <div className="rounded-2xl bg-[#1f2937] p-4">
                        <code className="text-white text-sm font-mono break-all">
                            {geminiCommand}
                        </code>
                    </div>
                </div>

                {/* Claude Code Command */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-stone-950">Claude Code</h4>
                            <a
                                href="https://docs.claude.com/en/docs/claude-code/overview"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-stone-500 transition-colors hover:text-stone-950"
                            >
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyToClipboard(claudeCommand, "Claude Code command copied!")}
                            className="h-8 rounded-full px-2"
                        >
                            <Copy className="w-4 h-4" />
                        </Button>
                    </div>
                    <div className="rounded-2xl bg-[#1f2937] p-4">
                        <code className="text-white text-sm font-mono break-all">
                            {claudeCommand}
                        </code>
                    </div>
                </div>
                {!providedApiKey && (
                    <div className="text-center py-2">
                        <p className="text-xs text-stone-500">
                            Replace YOUR_API_KEY with your actual API key
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default CodeAgentInstall;
